import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { defaultShouldDehydrateQuery, type Query } from '@tanstack/react-query';
import { del, get, set } from 'idb-keyval';

/**
 * The identity scopes (current user id + workspace id) a persisted query key
 * must be keyed under. Fed by main.tsx at boot and useWorkspaceStore on
 * switch — this module stays import-cycle-free (authService and the
 * workspace store both import IT for the clear hooks).
 */
const persistScopeIds = new Set<string>();

export function setPersistScopeIds(ids: Array<string | null | undefined>): void {
  persistScopeIds.clear();
  for (const id of ids) {
    if (id) persistScopeIds.add(id);
  }
}

export function addPersistScopeId(id: string | null | undefined): void {
  if (id) persistScopeIds.add(id);
}

/**
 * IndexedDB-backed persister (perf plan §4.6). idb-keyval stores the
 * persist-client package's serialized cache under ONE IndexedDB record, so a
 * warm repeat console load can paint real data (workspace, company, last
 * report, coverage, competitors) before any network round-trip resolves.
 *
 * Uses `PersistQueryClientProvider` (see App.tsx) rather than a hand-rolled
 * restore effect — it owns the restore-then-subscribe sequencing so a mutation
 * firing mid-restore can't race the hydrate.
 */
const PERSIST_STORAGE_KEY = 'knitknot-console-query-cache-v1';

export const queryPersister = createAsyncStoragePersister({
  storage: {
    getItem: (key) => get(key),
    setItem: (key, value) => set(key, value),
    removeItem: (key) => del(key),
  },
  key: PERSIST_STORAGE_KEY,
});

/**
 * Did the persister actually restore any queries from IndexedDB this boot?
 * This — not the in-memory cache size — is the RUM warm/cold signal: the boot
 * prefetch in main.tsx fills the in-memory cache on EVERY authed load, so
 * `getQueryCache().getAll().length > 0` would report a first-ever/expired/
 * busted cold start as "warm". The wrap records what the dehydrated state
 * carried before hydration.
 */
let restoredFromDisk = false;

export function didRestoreFromDisk(): boolean {
  return restoredFromDisk;
}

const _restoreClient = queryPersister.restoreClient.bind(queryPersister);
queryPersister.restoreClient = async () => {
  const persisted = await _restoreClient();
  restoredFromDisk = (persisted?.clientState?.queries?.length ?? 0) > 0;
  return persisted;
};

/**
 * Wipe the persisted cache. Call this on BOTH sign-out (`authService`'s
 * `signOut`/`clearAuthState`) and workspace switch (`useWorkspaceStore.
 * setWorkspaceId`) — the two moments the plan calls out as required clear
 * points. Every persisted key is workspace-scoped already (see the whitelist
 * below), so a stale entry from a prior tenant would never actually be read
 * back into the wrong workspace's UI; clearing here is belt-and-suspenders
 * hygiene against unbounded IndexedDB growth across accounts sharing a
 * browser profile, not a correctness fix. Safe to call even if nothing was
 * ever persisted (idb-keyval `del` on a missing key is a no-op).
 */
export function clearPersistedQueryCache(): Promise<void> {
  return Promise.resolve(queryPersister.removeClient()).catch(() => undefined);
}

// Query-key roots allowed into the persisted cache (perf plan §4.6
// whitelist — deliberately narrow to slow-changing entity reads). Every
// other query — eval lists, prompt lists, source/citation payloads, playbook
// lists, anything paginated — stays OUT. Several of those routinely carry
// 300KB-3MB payloads (see PERF_LOAD_OPTIMIZATION_PLAN.md §1.1) and would
// blow the persisted-cache budget for a surface that already refetches fast
// and changes often; persisting them buys nothing.
const PERSISTED_QUERY_KEY_ROOTS: ReadonlySet<string> = new Set([
  'workspaces', // queryKeys.workspace.list
  'workspace', // queryKeys.workspace.detail
  'company', // queryKeys.company.detail
  'report', // queryKeys.reports.detail
  'coverage', // queryKeys.coverage.detail
  'competitors', // queryKeys.competitors.list
  'competitor-detail', // queryKeys.competitors.detail
]);

// Belt-and-suspenders size guard — never persist a query whose serialized
// payload exceeds this, even if its key root is whitelisted above. Catches
// an unexpectedly large response on an otherwise-slow-changing key before it
// can blow the IndexedDB budget.
const MAX_PERSISTED_QUERY_BYTES = 100_000; // 100KB

function isSmallEnoughToPersist(query: Query): boolean {
  try {
    return JSON.stringify(query.state.data ?? null).length <= MAX_PERSISTED_QUERY_BYTES;
  } catch {
    // Non-serializable data (shouldn't happen for API responses) — don't persist it.
    return false;
  }
}

/**
 * Whitelist-only dehydrate predicate for `PersistQueryClientProvider`'s
 * `dehydrateOptions`. `defaultShouldDehydrateQuery` already excludes any
 * query that isn't in the `success` state — a query that failed with a 401
 * (apiClient throws on non-2xx) is never handed to us here in the first
 * place, so an auth error can never end up in the persisted cache.
 */
export function shouldDehydrateQuery(query: Query): boolean {
  if (!defaultShouldDehydrateQuery(query)) return false;
  const root = query.queryKey[0];
  if (typeof root !== 'string' || !PERSISTED_QUERY_KEY_ROOTS.has(root)) return false;
  // Tenant-scoping is ENFORCED, not assumed (safety review): every persisted
  // entry's second key segment must be the CURRENT workspace or user id
  // (registered via setPersistScopeIds/addPersistScopeId). Today all
  // whitelisted roots are keyed that way by convention — this guard makes
  // sure a future globally-keyed query under a whitelisted root can never be
  // written to a shared browser profile under someone else's identity.
  const scope = query.queryKey[1];
  if (typeof scope !== 'string' || scope.length === 0) return false;
  if (!persistScopeIds.has(scope)) return false;
  return isSmallEnoughToPersist(query);
}
